import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

function getToken() {
  return sessionStorage.getItem('wp_token');
}

function nextStatusLabel(status) {
  if (status === 'published') return 'Archive';
  if (status === 'archived') return 'Draft';
  return 'Publish';
}

function nextStatus(status) {
  if (status === 'published') return 'archived';
  if (status === 'archived') return 'draft';
  return 'published';
}

export default function AdminRoster() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/publishing/admin/roster', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed to load roster (${res.status})`);
      const data = await res.json();
      setProfiles(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const handleStatusToggle = async (profile) => {
    try {
      const res = await fetch(`/api/publishing/admin/roster/${profile.id}/status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: nextStatus(profile.status) }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      await fetchRoster();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Delete "${profile.display_name}"? This action cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/publishing/admin/roster/${profile.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error('Failed to delete profile');
      await fetchRoster();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReorder = async (profile, direction) => {
    try {
      const res = await fetch('/api/publishing/admin/roster/reorder', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: profile.id, direction }),
      });
      if (!res.ok) throw new Error('Failed to reorder');
      await fetchRoster();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <>
      <div className="wp-portal__page-header">
        <div>
          <h1 className="wp-portal__page-title">Roster</h1>
          <p className="wp-portal__page-sub">Manage publishing roster profiles.</p>
        </div>
        <button
          className="wp-portal__btn wp-portal__btn--primary"
          onClick={() => navigate('/app/portal/admin/roster/new')}
        >
          + New Profile
        </button>
      </div>

      {loading && (
        <p className="wp-portal__page-sub">Loading roster...</p>
      )}

      {!loading && error && (
        <div className="wp-portal__error">{error}</div>
      )}

      {!loading && !error && profiles.length === 0 && (
        <div className="wp-portal__empty">
          <p className="wp-portal__empty-label">
            No roster profiles. Create the first one.
          </p>
        </div>
      )}

      {!loading && !error && profiles.length > 0 && (
        <div className="wp-portal__table-wrap">
          <table className="wp-portal__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Category</th>
                <th>Status</th>
                <th>Featured</th>
                <th>Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile, idx) => (
                <tr key={profile.id}>
                  <td>{profile.display_name}</td>
                  <td>
                    <code className="wp-portal__code">{profile.slug}</code>
                  </td>
                  <td>{profile.professional_category || '—'}</td>
                  <td>
                    <span className={`wp-portal__badge wp-portal__badge--${profile.status}`}>
                      {profile.status}
                    </span>
                  </td>
                  <td>{profile.is_featured ? 'Yes' : 'No'}</td>
                  <td>{profile.display_order ?? '—'}</td>
                  <td className="wp-portal__actions">
                    <button
                      className="wp-portal__btn wp-portal__btn--sm"
                      onClick={() => navigate(`/app/portal/admin/roster/${profile.id}/edit`)}
                    >
                      Edit
                    </button>
                    <button
                      className="wp-portal__btn wp-portal__btn--sm wp-portal__btn--ghost"
                      onClick={() => handleStatusToggle(profile)}
                    >
                      {nextStatusLabel(profile.status)}
                    </button>
                    <button
                      className="wp-portal__btn wp-portal__btn--sm wp-portal__btn--ghost"
                      onClick={() => handleReorder(profile, 'up')}
                      disabled={idx === 0}
                      title="Move up"
                    >
                      &#8593;
                    </button>
                    <button
                      className="wp-portal__btn wp-portal__btn--sm wp-portal__btn--ghost"
                      onClick={() => handleReorder(profile, 'down')}
                      disabled={idx === profiles.length - 1}
                      title="Move down"
                    >
                      &#8595;
                    </button>
                    <button
                      className="wp-portal__btn wp-portal__btn--sm wp-portal__btn--danger"
                      onClick={() => handleDelete(profile)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
